import { SortingState } from '@hub/libs/bearing-hub.helper';
import { Icon, IconID } from '@hub/common/Icon/Icon';
import type { Headers } from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import {
  RangeDirectionChange,
  useBearingsTableStore,
} from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import type { ShapeDescriptor } from '@hub/bearing-hub/common/shapes/shapes';
import type { DimensionKey, IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import { dimensionKey, getDimensionLabel } from '@hub/bearing-hub/common/dimensions/dimensions';
import type { UnitSystem } from '@igus/kopla-domain';
import { getLengthCode } from '@hub/bearing-hub/common/useUnitSystem/useUnitSystem';

export interface DimensionsHeaderProps {
  selectedShape: ShapeDescriptor;
  text: string;
  unitSystem: UnitSystem;
  isFullScreen?: boolean;
}

export const DimensionsHeader = ({ selectedShape, text, unitSystem, isFullScreen }: DimensionsHeaderProps) => {
  const {
    allShapeDimensionsHeadersKeys,
    presentDimensionsDataFrom,
    presentDimensionsDataTo,
    updateChangeRangeDirection,
  } = useBearingsTableStore();
  const { tableHeaders, setTableHeadersSortingState } = useBearingsTableStore();

  const onSort = (sorting: boolean, key: Headers): void => {
    if (!sorting) {
      return;
    }
    setTableHeadersSortingState(key);
  };

  return (
    <div
      className={`flex flex-col leading-[2.4] overflow-hidden max-w-[${isFullScreen ? '300px' : '500px'}]`}
      data-testid="dimensions-header-container"
    >
      <div className="bg-[#333333] w-full text-center" data-testid="dimensions-header-head">
        {text} ({getLengthCode(unitSystem)})
      </div>

      <div className="flex flex-row relative bg-[#191919]">
        <div
          className={`absolute left-[-5px] bottom-0.5 cursor-pointer ${
            presentDimensionsDataFrom === 0 ? 'invisible' : 'visible'
          }`}
          onClick={() => updateChangeRangeDirection(RangeDirectionChange.Back)}
          data-testid="show-previous-dimensions-header"
        >
          <Icon size={8} id={IconID.CHEVRON_LEFT} />
        </div>
        <div className="flex flex-row w-full cursor-pointer justify-center">
          {tableHeaders
            .filter(h => allShapeDimensionsHeadersKeys.includes(h.key as keyof IbhDimensions))
            .map(header => {
              let dimensionLabel: Headers | 'b2.2' | 'b2.1' | 'T (°)' | 's' = header.key;
              const headerKey = header.key as DimensionKey;
              if (Object.values(dimensionKey).includes(headerKey)) {
                dimensionLabel = getDimensionLabel(headerKey, selectedShape.shapeKey) as
                  'b2.2' | 'b2.1' | 'T (°)' | 's';
              }

              return (
                <div
                  key={header.key}
                  data-testid={`cell-dimensions-header-${header.key}`}
                  className={`flex flex-row text-center items-center select-none justify-center min-w-[50px] w-full ${
                    header.sortingState !== SortingState.NoSort
                      ? 'text-igus-primary font-medium select-none'
                      : 'text-white font-normal'
                  }`}
                  onClick={() => onSort(header.sorting, header.key)}
                >
                  <div className="pl-4">{dimensionLabel}</div>
                  <div
                    data-testid={`sort-icon-container-dimensions-${header.key}`}
                    className={`ml-2 flex mt-1 ${
                      header.sortingState === SortingState.NoSort ? 'invisible' : 'visible'
                    } ${header.sortingState === SortingState.Descending ? 'rotate-180' : 'rotate-0'}`}
                  >
                    <Icon size={5} id={IconID.ARROW_TABLE_SORTING} />
                  </div>
                </div>
              );
            })}
        </div>
        <div
          className={`absolute bottom-0.5 cursor-pointer right-[-5px] ${
            presentDimensionsDataTo >= allShapeDimensionsHeadersKeys.length ? 'invisible' : 'visible'
          }`}
          onClick={() => updateChangeRangeDirection(RangeDirectionChange.Forward)}
          data-testid="show-next-dimensions-header"
        >
          <Icon size={8} id={IconID.CHEVRON_RIGHT} />
        </div>
      </div>
    </div>
  );
};
